import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Box } from "@mui/material";
import moment from "moment";

import { ActionButton, CardLayout, InputField, SelectField } from "../v3";
import { ProjectService, ToolService } from "../../services";
import { getFilteredOptions, selectAllOption } from "../../utils";
import { toolReportValidation } from "../../pages/report/validation";

export const ToolReportForm = ({ reportType = "summary", handleClear = () => {}, onGenerate = () => {} }) => {
  const [selectedTools, setSelectedTools] = useState([selectAllOption]);
  const [selectedProjects, setSelectedProjects] = useState([selectAllOption]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [reportDetails, setReportDetails] = useState({
    toolIds: null,
    projectIds: null,
    fromDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });

  useEffect(() => {
    handleClear();
    setLoading(false);
  }, [reportType]);

  const updateDetailsState = (event) => {
    setLoading(false);
    handleClear();
    const { name, value } = event.target;
    setReportDetails({ ...reportDetails, [name]: value });
    if (name === "fromDate") setErrors((prev) => ({ ...prev, fromDate: "" }));
    else if (name === "toDate") setErrors((prev) => ({ ...prev, toDate: "" }));
  };

  const handleToolChange = (tools) => {
    setLoading(false);
    handleClear();
    const allSelected = tools.some((tool) => tool.id === "all");

    if (allSelected) {
      setSelectedTools([selectAllOption]);
      setReportDetails((prev) => ({ ...prev, toolIds: null }));
    } else {
      // remove "all" if previously selected
      const filtered = tools.filter((tool) => tool.id !== "all");
      setSelectedTools(filtered);
      setReportDetails((prev) => ({
        ...prev,
        toolIds: filtered.map((tool) => tool.id),
      }));
    }

    setErrors((prev) => ({ ...prev, toolIds: "" }));
  };

  const handleProjectChange = (projects) => {
    setLoading(false);
    handleClear();
    const allSelected = projects.some((project) => project.id === "all");

    if (allSelected) {
      setSelectedProjects([selectAllOption]);
      setReportDetails((prev) => ({ ...prev, projectIds: null }));
    } else {
      // remove "all" if previously selected
      const filtered = projects.filter((project) => project.id !== "all");
      setSelectedProjects(filtered);
      setReportDetails((prev) => ({ ...prev, projectIds: filtered.map((project) => project.id) }));
    }

    setErrors((prev) => ({ ...prev, projectIds: "" }));
  };

  const handleGenerateReport = async () => {
    setErrors({});
    const validationErrors = toolReportValidation(reportDetails, selectedTools[0]?.id, selectedProjects[0]?.id);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);

      const isoFromDate = moment(reportDetails.fromDate).startOf("day").toISOString();
      const isoToDate = moment(reportDetails.toDate).endOf("day").toISOString();

      const payload = {
        ...reportDetails,
        fromDate: isoFromDate,
        toDate: isoToDate,
        reportType,
      };

      onGenerate?.(payload, {
        onError: () => setLoading(false),
      });
    } catch (error) {
      setLoading(false);
      toast.error("Failed to generate report");
      console.error("🚀 ~ handleGenerateReport ~ error:", error);
    }
  };

  return (
    <>
      <CardLayout>
        <Box mt={1}>
          <Box display="flex" gap={2}>
            <SelectField
              label="Tools"
              value={selectedTools}
              getData={ToolService.getTools}
              getDataStaticFilters={{ sortBy: "name", sortOrder: "asc" }}
              required
              multiSelect
              error={!!errors.toolIds}
              helperText={errors.toolIds}
              onChange={handleToolChange}
              optionFilter={(options) => getFilteredOptions(options, selectedTools, selectAllOption)}
              style={{ width: "100%" }}
            />
          </Box>
          <Box display="flex" gap={2}>
            <SelectField
              label="Project"
              value={selectedProjects}
              getData={ProjectService.getProjectList}
              getDataStaticFilters={{ sortBy: "name", sortOrder: "asc" }}
              required
              multiSelect
              error={!!errors.projectIds}
              helperText={errors.projectIds}
              onChange={handleProjectChange}
              optionFilter={(options) => getFilteredOptions(options, selectedProjects, selectAllOption)}
              style={{ width: "100%" }}
            />
          </Box>
          <Box display="flex" gap={2}>
            <InputField
              required
              type="date"
              label="From Date"
              name="fromDate"
              value={reportDetails.fromDate}
              onChange={updateDetailsState}
              error={!!errors.fromDate}
              helperText={errors.fromDate}
              fullWidth
            />
            <InputField
              required
              type="date"
              label="To Date"
              name="toDate"
              value={reportDetails.toDate}
              onChange={updateDetailsState}
              error={!!errors.toDate}
              helperText={errors.toDate}
              fullWidth
            />
          </Box>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <ActionButton buttonText="Generate" color="primary" disabled={loading} onButtonClick={handleGenerateReport} />
        </Box>
      </CardLayout>
    </>
  );
};
